/* ══════════════════════════════════════════════════
   THE 2000s POSTER STORE — THEME JS
   Shopify Ajax Cart · Product modal · Filters · Nav
══════════════════════════════════════════════════ */

'use strict';

/* ── CART STATE ─────────────────────────────────── */
const Cart = {
  /* Fetch current cart from Shopify */
  async get() {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    return res.json();
  },

  /* Add variant to cart */
  async add(variantId, quantity = 1) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity })
    });
    if (!res.ok) throw new Error('Could not add to cart');
    return res.json();
  },

  /* Update line item by key */
  async change(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    return res.json();
  },

  /* Remove line item */
  async remove(key) {
    return this.change(key, 0);
  }
};

/* ── TOAST ──────────────────────────────────────── */
const Toast = {
  el: null,
  msgEl: null,
  timer: null,

  init() {
    this.el = document.getElementById('toast');
    this.msgEl = document.getElementById('toastMsg');
  },

  show(msg = 'Added to cart') {
    if (!this.el) return;
    this.msgEl.textContent = msg;
    this.el.classList.add('show');
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.el.classList.remove('show'), 2800);
  }
};

/* ── CART DRAWER ────────────────────────────────── */
const CartDrawer = {
  drawer: null,
  overlay: null,
  itemsEl: null,
  totalEl: null,
  badgeEls: null,
  shippingEl: null,
  FREE_THRESHOLD: 5000, // cents ($50)

  init() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-overlay');
    this.itemsEl = document.getElementById('cart-drawer-items');
    this.totalEl = document.getElementById('cart-drawer-total');
    this.shippingEl = document.getElementById('cart-shipping-bar');
    this.badgeEls = document.querySelectorAll('.cart-count-badge');

    // Open cart on header button
    document.querySelectorAll('[data-open-cart]').forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });

    // Close on overlay / close btn
    this.overlay?.addEventListener('click', () => this.close());
    document.querySelectorAll('[data-close-cart]').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });
  },

  open() {
    this.refresh().then(() => {
      this.drawer?.classList.add('open');
      this.overlay?.classList.add('open');
      document.body.classList.add('no-scroll');
    });
  },

  close() {
    this.drawer?.classList.remove('open');
    this.overlay?.classList.remove('open');
    document.body.classList.remove('no-scroll');
  },

  async refresh() {
    const cart = await Cart.get();
    this.render(cart);
  },

  updateBadge(count) {
    this.badgeEls.forEach(el => {
      el.textContent = count;
      el.classList.toggle('visible', count > 0);
    });
  },

  fmtMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  },

  render(cart) {
    const { items, total_price, item_count } = cart;
    this.updateBadge(item_count);

    // Shipping bar
    if (this.shippingEl) {
      const remaining = this.FREE_THRESHOLD - total_price;
      this.shippingEl.textContent = remaining > 0
        ? `Add ${this.fmtMoney(remaining)} more for free shipping`
        : '✓ You qualify for free shipping!';
    }

    // Total
    if (this.totalEl) {
      this.totalEl.textContent = this.fmtMoney(total_price);
    }

    // Items
    if (!this.itemsEl) return;

    if (items.length === 0) {
      this.itemsEl.innerHTML = `
        <div class="cart-empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          <p>Your cart is empty</p>
        </div>`;
      return;
    }

    this.itemsEl.innerHTML = items.map(item => `
      <div class="cart-line-item" data-key="${item.key}">
        <div class="cart-item-thumb">
          <img src="${item.image}" alt="${item.title}" loading="lazy">
        </div>
        <div class="cart-item-info">
          <div class="cart-item-title">${item.product_title}</div>
          <div class="cart-item-variant">${item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
          <div class="cart-item-row">
            <div class="cart-qty-ctrl">
              <button class="cart-qty-btn" data-key="${item.key}" data-delta="-1">−</button>
              <span class="cart-qty-num">${item.quantity}</span>
              <button class="cart-qty-btn" data-key="${item.key}" data-delta="1">+</button>
            </div>
            <span class="cart-item-price">${this.fmtMoney(item.final_line_price)}</span>
            <button class="cart-item-remove" data-key="${item.key}" data-action="remove" aria-label="Remove">×</button>
          </div>
        </div>
      </div>`).join('');

    // Bind qty buttons
    this.itemsEl.querySelectorAll('.cart-qty-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const key = btn.dataset.key;
        const delta = parseInt(btn.dataset.delta);
        const row = btn.closest('.cart-line-item');
        const qtyEl = row.querySelector('.cart-qty-num');
        const newQty = Math.max(0, parseInt(qtyEl.textContent) + delta);
        const cart = await Cart.change(key, newQty);
        this.render(cart);
      });
    });

    // Remove buttons
    this.itemsEl.querySelectorAll('[data-action="remove"]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const cart = await Cart.remove(btn.dataset.key);
        this.render(cart);
      });
    });
  }
};

/* ── ADD TO CART ─────────────────────────────────── */
async function addToCart(variantId, productTitle, qty = 1) {
  try {
    await Cart.add(variantId, qty);
    const cart = await Cart.get();
    CartDrawer.updateBadge(cart.item_count);
    Toast.show(`${productTitle} added`);
  } catch (err) {
    console.error('Add to cart error:', err);
    Toast.show('Something went wrong');
  }
}

/* ── PRODUCT PAGE ────────────────────────────────── */
function initProductPage() {
  const form = document.getElementById('product-form');
  if (!form) return;

  const variantBtns = form.querySelectorAll('[data-variant-btn]');
  const variantInput = form.querySelector('[name="id"]');
  const priceEl = document.getElementById('product-price');
  const addBtn = document.getElementById('add-to-cart-btn');
  const qtyMinusBtn = document.getElementById('qty-minus');
  const qtyPlusBtn = document.getElementById('qty-plus');
  const qtyNumEl = document.getElementById('qty-num');

  let currentQty = 1;

  // Variant selection
  variantBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      variantBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      const variantId = btn.dataset.variantId;
      const price = parseInt(btn.dataset.price);
      const available = btn.dataset.available !== 'false';

      if (variantInput) variantInput.value = variantId;

      if (priceEl) {
        priceEl.textContent = '$' + (price / 100).toFixed(2);
      }

      if (addBtn) {
        if (available) {
          addBtn.textContent = 'Add to Cart';
          addBtn.disabled = false;
          addBtn.classList.remove('soldout');
          addBtn.dataset.variantId = variantId;
        } else {
          addBtn.textContent = 'Sold Out';
          addBtn.disabled = true;
          addBtn.classList.add('soldout');
        }
      }
    });
  });

  // Qty controls
  qtyMinusBtn?.addEventListener('click', () => {
    currentQty = Math.max(1, currentQty - 1);
    if (qtyNumEl) qtyNumEl.textContent = currentQty;
  });

  qtyPlusBtn?.addEventListener('click', () => {
    currentQty = Math.min(10, currentQty + 1);
    if (qtyNumEl) qtyNumEl.textContent = currentQty;
  });

  // Add to cart
  addBtn?.addEventListener('click', async (e) => {
    e.preventDefault();
    const variantId = addBtn.dataset.variantId || variantInput?.value;
    const title = form.dataset.productTitle || 'Item';
    if (!variantId || addBtn.disabled) return;

    addBtn.textContent = 'Adding...';
    addBtn.disabled = true;
    await addToCart(variantId, title, currentQty);
    addBtn.textContent = 'Add to Cart';
    addBtn.disabled = false;

    // Open cart drawer
    CartDrawer.open();
  });
}

/* ── COLLECTION FILTER ──────────────────────────── */
function initCollectionFilter() {
  const pills = document.querySelectorAll('[data-filter]');
  if (!pills.length) return;

  pills.forEach(pill => {
    pill.addEventListener('click', () => {
      const filter = pill.dataset.filter;

      pills.forEach(p => p.classList.remove('active'));
      pill.classList.add('active');

      document.querySelectorAll('[data-tags]').forEach(card => {
        if (filter === 'all') {
          card.classList.remove('card-hidden');
        } else {
          const tags = card.dataset.tags?.split(',') || [];
          card.classList.toggle('card-hidden', !tags.includes(filter));
        }
      });
    });
  });
}

/* ── HERO BG POSTERS ──────────────────────────────── */
function initHeroPosters() {
  const container = document.querySelector('.hero-bg-posters');
  if (!container) return;

  const images = Array.from(container.querySelectorAll('.hero-bg-poster img'));
  if (!images.length) return;

  // Random positions are set in Liquid, so just animate opacity in
  images.forEach((img, i) => {
    img.style.animationDelay = `${i * 0.15}s`;
  });
}

/* ── STICKY HEADER ──────────────────────────────── */
function initStickyHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  let lastY = 0;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    // Auto-hide on scroll down, show on scroll up (only after 200px)
    if (y > 200) {
      header.style.transform = y > lastY ? 'translateY(-100%)' : 'translateY(0)';
    } else {
      header.style.transform = 'translateY(0)';
    }
    lastY = y;
  }, { passive: true });
}

/* ── MOBILE NAV ─────────────────────────────────── */
function initMobileNav() {
  const toggleBtn = document.getElementById('mobile-nav-toggle');
  const mobileMenu = document.getElementById('mobile-menu');
  if (!toggleBtn || !mobileMenu) return;

  toggleBtn.addEventListener('click', () => {
    const open = mobileMenu.classList.toggle('open');
    toggleBtn.setAttribute('aria-expanded', open);
  });
}

/* ── EMAIL FORM ─────────────────────────────────── */
function initEmailForm() {
  const forms = document.querySelectorAll('.email-form');
  forms.forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const btn = form.querySelector('button[type="submit"]');
      if (!input?.value) return;

      btn.textContent = 'Subscribed!';
      btn.style.background = '#3a8a0a';
      input.value = '';
      setTimeout(() => {
        btn.textContent = 'Subscribe';
        btn.style.background = '';
      }, 3000);
    });
  });
}

/* ── QUICK ADD (cards on non-product pages) ────── */
function initQuickAdd() {
  document.querySelectorAll('[data-quick-add]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const variantId = btn.dataset.variantId;
      const title = btn.dataset.title || 'Poster';
      if (!variantId) return;
      await addToCart(variantId, title);
      CartDrawer.open();
    });
  });
}

/* ── SCROLL TRACK ARROWS ──────────────────────── */
function initScrollArrows() {
  document.querySelectorAll('[data-scroll-track-container]').forEach(wrapper => {
    const track = wrapper.querySelector('.scroll-track');
    const leftBtn = wrapper.querySelector('[data-scroll-left]');
    const rightBtn = wrapper.querySelector('[data-scroll-right]');
    if (!track) return;

    const STEP = 680;
    leftBtn?.addEventListener('click', () => track.scrollBy({ left: -STEP, behavior: 'smooth' }));
    rightBtn?.addEventListener('click', () => track.scrollBy({ left: STEP, behavior: 'smooth' }));
  });
}

/* ── INIT ───────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  Toast.init();
  CartDrawer.init();
  initProductPage();
  initCollectionFilter();
  initHeroPosters();
  initStickyHeader();
  initMobileNav();
  initEmailForm();
  initQuickAdd();
  initScrollArrows();

  // Load badge count on page load
  Cart.get().then(cart => CartDrawer.updateBadge(cart.item_count));
});
